import { Trash } from '@phosphor-icons/react';
import { useState } from 'react';
import { CONTENT_TYPES_LIST } from '../../constants/value';
import { saveGeneratedTypes } from '../../sources/type';
import translations from '../../translations';
import type {
    GeneratedTypeElementAPI,
    GeneratedTypeElementPropertyAPI,
    GeneratedTypeSetAPI,
} from '../../types/type';
import type { ContentType } from '../../types/value';
import { isNullOrEmpty } from '../../utils/guard';
import ButtonDefault from '../buttons/ButtonDefault';
import ButtonPrimary from '../buttons/ButtonPrimary';
import Footer from '../generic/Footer';
import FormGroup from '../generic/FormGroup';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import TypeReferencePicker from './TypeReferencePicker';
import { useTypes } from './TypesProvider';

interface Props {
    set: GeneratedTypeSetAPI;
    onBack: () => void;
}

// Working copies carry a client-only `key` so list rows stay stable as properties are added/removed.
type EditableProperty = GeneratedTypeElementPropertyAPI & { key: string };
type EditableType = Omit<GeneratedTypeElementAPI, 'properties'> & {
    properties: EditableProperty[];
};

const isComplexType = (contentType: ContentType) =>
    contentType === 'ContentObject' || contentType === 'ContentList';

const addPropertyKeys = (types: GeneratedTypeElementAPI[]): EditableType[] =>
    types.map((type) => ({
        ...type,
        properties: type.properties.map((property) => ({ ...property, key: crypto.randomUUID() })),
    }));

const removePropertyKeys = (types: EditableType[]): GeneratedTypeSetAPI => ({
    types: types.map((type) => ({
        ...type,
        properties: type.properties.map(({ key, ...property }) => property),
    })),
});

const GeneratedTypesPreview = ({ set, onBack }: Props) => {
    const [types, setTypes] = useState<EditableType[]>(() => addPropertyKeys(set.types));
    const [isSaving, setIsSaving] = useState(false);

    const { returnToTypeList } = useTypes();
    const dispatchNotification = useNotificationDispatch();

    const nameCounts = types.reduce<Record<string, number>>((counts, type) => {
        const name = type.developerName.trim();
        counts[name] = (counts[name] ?? 0) + 1;
        return counts;
    }, {});

    const updateType = (typeIndex: number, updated: EditableType) => {
        setTypes(types.map((type, index) => (index === typeIndex ? updated : type)));
    };

    const updateTypeName = (typeIndex: number, developerName: string) => {
        const type = types[typeIndex];
        if (type) {
            updateType(typeIndex, { ...type, developerName });
        }
    };

    const removeType = (typeIndex: number) => {
        const removed = types[typeIndex];
        if (!removed) {
            return;
        }

        // Drop the type and clear any properties in the remaining types that referenced it.
        setTypes(
            types
                .filter((_, index) => index !== typeIndex)
                .map((type) => ({
                    ...type,
                    properties: type.properties.map((property) =>
                        property.typeReference === removed.reference
                            ? { ...property, typeReference: null }
                            : property,
                    ),
                })),
        );
    };

    const updatePropertyName = (
        typeIndex: number,
        propertyIndex: number,
        developerName: string,
    ) => {
        const type = types[typeIndex];
        if (!type) {
            return;
        }
        updateType(typeIndex, {
            ...type,
            properties: type.properties.map((property, index) =>
                index === propertyIndex ? { ...property, developerName } : property,
            ),
        });
    };

    const updatePropertyContentType = (
        typeIndex: number,
        propertyIndex: number,
        contentType: ContentType,
    ) => {
        const type = types[typeIndex];
        if (!type) {
            return;
        }
        updateType(typeIndex, {
            ...type,
            properties: type.properties.map((property, index) =>
                index === propertyIndex
                    ? {
                          ...property,
                          contentType,
                          // A reference only applies to object/list properties.
                          typeReference: isComplexType(contentType) ? property.typeReference : null,
                          typeElementId: isComplexType(contentType) ? property.typeElementId : null,
                      }
                    : property,
            ),
        });
    };

    const updatePropertyReference = (
        typeIndex: number,
        propertyIndex: number,
        selection: { typeReference: string | null; typeElementId: string | null },
    ) => {
        const type = types[typeIndex];
        if (!type) {
            return;
        }

        updateType(typeIndex, {
            ...type,
            properties: type.properties.map((property, index) =>
                index === propertyIndex ? { ...property, ...selection } : property,
            ),
        });
    };

    const addProperty = (typeIndex: number) => {
        const type = types[typeIndex];
        if (!type) {
            return;
        }
        updateType(typeIndex, {
            ...type,
            properties: [
                ...type.properties,
                {
                    key: crypto.randomUUID(),
                    developerName: '',
                    contentType: 'ContentString',
                    contentFormat: null,
                    typeReference: null,
                    typeElementId: null,
                },
            ],
        });
    };

    const removeProperty = (typeIndex: number, propertyIndex: number) => {
        const type = types[typeIndex];
        if (!type) {
            return;
        }
        updateType(typeIndex, {
            ...type,
            properties: type.properties.filter((_, index) => index !== propertyIndex),
        });
    };

    const getTypeNameError = (type: EditableType) => {
        const name = type.developerName.trim();
        if (isNullOrEmpty(name)) {
            return translations.TYPES_name_validation_message;
        }
        if ((nameCounts[name] ?? 0) > 1) {
            return translations.TYPES_generate_type_collision_duplicate;
        }
        return null;
    };

    const hasInvalidProperty = types.some((type) =>
        type.properties.some(
            (property) =>
                isNullOrEmpty(property.developerName.trim()) ||
                (isComplexType(property.contentType) &&
                    isNullOrEmpty(property.typeReference) &&
                    isNullOrEmpty(property.typeElementId)),
        ),
    );

    const hasTypeNameError = types.some((type) => getTypeNameError(type) !== null);

    const canSave = !isSaving && !hasTypeNameError && !hasInvalidProperty;

    const onSave = async () => {
        try {
            setIsSaving(true);
            await saveGeneratedTypes(removePropertyKeys(types));
            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: translations.TYPES_generate_type_save_success,
                },
            });
            returnToTypeList();
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className="admin-page generated-types-preview">
                <h1>{translations.TYPES_generate_type_preview_title}</h1>
                <p>{translations.TYPES_generate_type_preview_description}</p>
                {types.map((type, typeIndex) => {
                    const typeNameError = getTypeNameError(type);

                    return (
                        <div key={type.reference} className="generated-type-card">
                            {types.length > 1 ? (
                                <button
                                    type="button"
                                    className="table-icon table-icon-delete generated-type-delete"
                                    title={
                                        translations.TYPES_generate_type_remove_type_button_label
                                    }
                                    aria-label={
                                        translations.TYPES_generate_type_remove_type_button_label
                                    }
                                    onClick={() => removeType(typeIndex)}
                                >
                                    <Trash />
                                </button>
                            ) : null}
                            <FormGroup
                                label={translations.TYPES_generate_type_type_name_label}
                                htmlFor={`generated-type-name-${type.reference}`}
                                isRequired
                                validationMessage={typeNameError ?? ''}
                                isValid={typeNameError === null}
                                showValidation={typeNameError !== null}
                            >
                                <input
                                    id={`generated-type-name-${type.reference}`}
                                    className="form-control form-control-long"
                                    value={type.developerName}
                                    onChange={(e) => updateTypeName(typeIndex, e.target.value)}
                                    type="text"
                                />
                            </FormGroup>
                            <div className="generated-type-properties">
                                <div className="generated-type-property-header">
                                    <span>
                                        {translations.TYPES_generate_type_property_name_label}
                                    </span>
                                    <span>
                                        {translations.TYPES_generate_type_property_type_label}
                                    </span>
                                    <span>
                                        {translations.TYPES_generate_type_property_references_label}
                                    </span>
                                    <span />
                                </div>
                                {type.properties.map((property, propertyIndex) => {
                                    const isComplex = isComplexType(property.contentType);
                                    const referenceMissing =
                                        isComplex &&
                                        isNullOrEmpty(property.typeReference) &&
                                        isNullOrEmpty(property.typeElementId);

                                    return (
                                        <div className="generated-type-property" key={property.key}>
                                            <input
                                                className="form-control"
                                                value={property.developerName}
                                                placeholder={
                                                    translations.TYPES_generate_type_property_name_label
                                                }
                                                aria-label={
                                                    translations.TYPES_generate_type_property_name_label
                                                }
                                                onChange={(e) =>
                                                    updatePropertyName(
                                                        typeIndex,
                                                        propertyIndex,
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                            <select
                                                className="form-control"
                                                value={property.contentType}
                                                aria-label={
                                                    translations.TYPES_generate_type_property_type_label
                                                }
                                                onChange={(e) =>
                                                    updatePropertyContentType(
                                                        typeIndex,
                                                        propertyIndex,
                                                        e.target.value as ContentType,
                                                    )
                                                }
                                            >
                                                {CONTENT_TYPES_LIST.map(({ key, label }) => (
                                                    <option key={key} value={key}>
                                                        {label}
                                                    </option>
                                                ))}
                                            </select>
                                            {isComplex ? (
                                                <TypeReferencePicker
                                                    generatedTypes={types}
                                                    typeReference={property.typeReference}
                                                    typeElementId={property.typeElementId}
                                                    isInvalid={referenceMissing}
                                                    onChange={(selection) =>
                                                        updatePropertyReference(
                                                            typeIndex,
                                                            propertyIndex,
                                                            selection,
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <span />
                                            )}
                                            <button
                                                type="button"
                                                className="table-icon table-icon-delete"
                                                title={
                                                    translations.TYPES_generate_type_remove_property_button_label
                                                }
                                                aria-label={
                                                    translations.TYPES_generate_type_remove_property_button_label
                                                }
                                                onClick={() =>
                                                    removeProperty(typeIndex, propertyIndex)
                                                }
                                            >
                                                <Trash />
                                            </button>
                                        </div>
                                    );
                                })}
                                <ButtonDefault
                                    className="margin-top-small"
                                    title={
                                        translations.TYPES_generate_type_add_property_button_label
                                    }
                                    onClick={() => addProperty(typeIndex)}
                                >
                                    {translations.TYPES_generate_type_add_property_button_label}
                                </ButtonDefault>
                            </div>
                        </div>
                    );
                })}
            </div>
            <Footer>
                <ButtonDefault
                    title={translations.COMMON_back}
                    className="flex-child-right"
                    onClick={onBack}
                    disabled={isSaving}
                >
                    {translations.COMMON_back}
                </ButtonDefault>
                <ButtonPrimary
                    title={translations.COMMON_save}
                    onClick={onSave}
                    disabled={!canSave}
                >
                    {translations.COMMON_save}
                </ButtonPrimary>
            </Footer>
        </>
    );
};

export default GeneratedTypesPreview;
